import type { AccountSummary } from '@personal-os/shared';
import { useState } from 'react';
import { regenerateRecoveryCodes } from './api';
import { formatWhen } from './format';
import { describeAuthError } from './passkeys';
import type { RunSensitive } from './PasskeysCard';

export function RecoveryCodesCard({
  account,
  run,
  refresh,
  onCodes,
}: {
  account: AccountSummary;
  run: RunSensitive;
  refresh: () => Promise<unknown>;
  onCodes: (codes: string[]) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const { remaining, generatedAt } = account.recoveryCodes;

  async function make() {
    setBusy(true);
    setMessage(null);
    try {
      await run(async () => {
        const { recoveryCodes } = await regenerateRecoveryCodes();
        await refresh();
        onCodes(recoveryCodes);
      });
    } catch (err) {
      setMessage(describeAuthError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card" aria-labelledby="recovery-title" data-testid="recovery-card">
      <h2 id="recovery-title" className="card-heading">
        Recovery codes
      </h2>
      <p className="hint">
        One-time codes for when your phone and authenticator are both gone. Each works once.
      </p>
      <p className="status-text" data-testid="recovery-remaining">
        {generatedAt
          ? `${remaining} of 10 unused · made ${formatWhen(generatedAt)}`
          : 'None yet. They are made when you turn on the authenticator app.'}
      </p>
      <button type="button" disabled={busy} onClick={() => void make()}>
        {generatedAt ? 'Make new codes (replaces the old ones)' : 'Make recovery codes'}
      </button>
      {message && (
        <p role="alert" className="error-text">
          {message}
        </p>
      )}
    </section>
  );
}
